import React, { useState, useEffect, useCallback, useRef } from 'react';
import { readBlobError, triggerBlobDownload } from '../utils/exportHelpers';
import jsQRLib from 'jsqr';
import { PageHeader, StatCards, Card, TabBar, Table, Field, inputStyle, btnPrimary, btnSm, Empty, useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import axiosClient from '../api/client';
import { getBrandingSlug } from '../utils/formatters';
import { printGatePass } from '../utils/print';
import { qrToDataURL, qrToCanvas } from '../utils/qrcode';

const TYPE_LABELS = {
  guest: 'Hotel Guest', visitor: 'Visitor', conference: 'Conference',
  delivery: 'Delivery', staff: 'Staff', other: 'Other',
};
const TYPE_COLORS = {
  guest: '#3498db', visitor: '#22c55e', conference: '#9b59b6',
  delivery: '#f59e0b', staff: '#c45e3e', other: '#64748b',
};
const TYPE_ICONS = {
  guest: '🏨', visitor: '🙋', conference: '🏛️',
  delivery: '📦', staff: '👔', other: '🚗',
};

const DEPT_LABELS = {
  kitchen: 'Kitchen', bar: 'Bar', housekeeping: 'Housekeeping',
  restaurant: 'Restaurant', reception: 'Reception', conference: 'Conference',
  maintenance: 'Maintenance', pool: 'Pool & Recreation', spa: 'Spa & Wellness',
  gift_shop: 'Gift Shop', admin: 'Administration', other: 'Other',
};

const blankForm = {
  name: '', id_number: '', phone: '', car_plate: '',
  visitor_type: 'visitor', department: '', purpose: '', host_name: '', notes: '',
};

// generateQRDataURL now delegates to the real QR library in utils/qrcode.js (#10/#67)
function generateQRDataURL(text, size = 200) {
  return qrToDataURL(text, size);
}

// Build the scan URL that gate staff will scan
function buildScanURL(token) {
  const base = window.location.origin;
  return `${base}/gate-scan/${token}`;
}

export default function GatePage({ notify }) {
  const bp = useBreakpoint();
  const [tab, setTab]             = useState('register');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(0);
  const [analyticsLimit, setAnalyticsLimit] = useState(20);
  const [visitors, setVisitors]   = useState([]);
  const [inside, setInside]       = useState([]);
  const [today, setToday]         = useState([]);
  const [form, setForm]           = useState(blankForm);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [lastEntry, setLastEntry] = useState(null);
  const nameRef = useRef(null);

  // Export state
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo]     = useState('');
  const [exportType, setExportType]         = useState('');
  const [exportStatus, setExportStatus]     = useState('');
  const [exporting, setExporting]           = useState(null); // 'excel' | 'pdf' | null

  // Log tab date filter state
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo]     = useState('');

  // QR Scanner state
  const [qrInput, setQrInput]         = useState('');
  const [scanResult, setScanResult]   = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError]     = useState('');
  const [confirmExit, setConfirmExit] = useState(null);
  const qrInputRef  = useRef(null);

  // Camera scanner state
  const [cameraOpen, setCameraOpen]     = useState(false);
  const [cameraError, setCameraError]   = useState('');
  const [camScanning, setCamScanning]   = useState(false);
  const videoRef    = useRef(null);
  const canvasRef2  = useRef(null);
  const streamRef   = useRef(null);
  const rafRef      = useRef(null);

  // Get the jsQR decoder — imported at top of file
  const getJsQR = () => {
    if (typeof jsQRLib === 'function') return jsQRLib;
    if (jsQRLib && typeof jsQRLib.default === 'function') return jsQRLib.default;
    return null;
  };

  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setCamScanning(false);
    setCameraError('');
  };

  const startCamera = async () => {
    setCameraError('');
    setCameraOpen(true);
    setCamScanning(true);
    try {
      const jsQR = getJsQR();
      if (!jsQR) throw new Error('QR decoder unavailable');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      // Wait for video element to be in DOM
      await new Promise(r => setTimeout(r, 120));
      if (!videoRef.current) return stopCamera();
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      const tick = () => {
        const video = videoRef.current;
        const canvas = canvasRef2.current;
        if (!video || !canvas || video.readyState < 2) { rafRef.current = requestAnimationFrame(tick); return; }
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) {
          stopCamera();
          handleQrScan(code.data);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch(e) {
      setCamScanning(false);
      setCameraError(e.name === 'NotAllowedError'
        ? 'Camera permission denied. Please allow camera access and try again.'
        : e.message || 'Could not access camera');
    }
  };

  // Clean up camera on unmount or tab change
  useEffect(() => { return () => stopCamera(); }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const r = await api.getGateFrequentVisitors(analyticsLimit, analyticsDays);
      setAnalytics(r.data);
    } catch { /* silent */ }
    finally { setAnalyticsLoading(false); }
  }, [analyticsLimit, analyticsDays]);

  useEffect(() => { if (tab === 'analytics') loadAnalytics(); }, [tab, loadAnalytics]);

    const load = useCallback(async () => {
    const todayDate = new Date().toISOString().slice(0, 10);
    try {
      const [ins, tod] = await Promise.all([
        api.getGateVisitors('?status=in'),
        api.getGateVisitors(`?date=${todayDate}`),
      ]);
      setInside(ins.data.results || ins.data);
      setToday(tod.data.results || tod.data);
      // Only reload visitors if no date filter is active (avoid overwriting filtered view)
      if (!logDateFrom && !logDateTo) {
        const all = await api.getGateVisitors();
        setVisitors(all.data.results || all.data);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate fetch for the log tab with date filter applied
  const loadLogFiltered = useCallback(async () => {
    try {
      let params = '?';
      if (logDateFrom) params += `date_from=${logDateFrom}&`;
      if (logDateTo)   params += `date_to=${logDateTo}&`;
      const res = await api.getGateVisitors(params);
      setVisitors(res.data.results || res.data);
    } catch {}
  }, [logDateFrom, logDateTo]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  // Re-fetch log whenever log date filters change
  useEffect(() => {
    loadLogFiltered();
  }, [loadLogFiltered]);

  useEffect(() => {
    if (tab === 'register') nameRef.current?.focus();
    if (tab === 'scan') {
      setTimeout(() => qrInputRef.current?.focus(), 100);
    }
  }, [tab]);

  const setPlate = (val) => setForm(f => ({ ...f, car_plate: val.toUpperCase() }));

  const register = async () => {
    if (!form.name.trim()) { notify('Name is required', 'error'); return; }
    if (!form.id_number.trim()) { notify('ID / Passport number is required', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.createGateVisitor(form);
      const entry = res.data;
      setLastEntry(entry);
      await load();
      setForm(blankForm);
      notify(`${entry.name} registered — Pass #${entry.id}`);
      if (nameRef.current) nameRef.current.focus();
    } catch(e) {
      notify(e.response?.data?.error || 'Error registering visitor', 'error');
    } finally { setLoading(false); }
  };

  const markExit = async (id, name) => {
    try {
      await api.exitGateVisitor(id);
      await load();
      notify(`✓ ${name} exit recorded`);
    } catch(e) { notify('Error recording exit', 'error'); }
  };

  // ── QR SCAN LOGIC ──────────────────────────────────────────────────────────
  // The gate staff either:
  //  a) Use a handheld barcode scanner (auto-types the URL + Enter) — handled by onKeyDown
  //  b) Manually paste/type the token or URL
  const handleQrScan = async (raw) => {
    if (!raw.trim()) return;
    setScanLoading(true);
    setScanError('');
    setScanResult(null);
    setConfirmExit(null);

    // Extract token — handle full URL or bare token
    let token = raw.trim();
    const match = token.match(/gate-scan\/([a-f0-9]+)/i) || token.match(/([a-f0-9]{32})/);
    if (match) token = match[1];

    try {
      const res = await api.lookupGateQR(token);
      const visitor = res.data;
      setScanResult(visitor);
      if (visitor.status === 'out') {
        setScanError(`Already exited at ${new Date(visitor.time_out).toLocaleTimeString('en-KE')}`);
      }
    } catch(e) {
      if (e.response?.status === 404) setScanError('Invalid QR code — pass not found');
      else setScanError(e.response?.data?.error || 'Scan failed');
    } finally {
      setScanLoading(false);
      setQrInput('');
    }
  };

  const confirmExitScan = async () => {
    if (!scanResult) return;
    setScanLoading(true);
    try {
      const token = scanResult.qr_token;
      const res = await api.scanExitGateVisitor(token);
      setScanResult(res.data);
      setScanError('');
      notify(`✓ ${res.data.name} — exit recorded via QR scan`);
      setConfirmExit(null);
      await load();
    } catch(e) {
      setScanError(e.response?.data?.error || 'Exit failed');
    } finally { setScanLoading(false); }
  };

  // ── EXPORT LOGIC ───────────────────────────────────────────────────────────
  const buildExportParams = () => {
    let params = '?';
    if (exportDateFrom) params += `date_from=${exportDateFrom}&`;
    if (exportDateTo)   params += `date_to=${exportDateTo}&`;
    if (exportType)     params += `visitor_type=${exportType}&`;
    if (exportStatus)   params += `status=${exportStatus}&`;
    return params;
  };

  const doExportExcel = async () => {
    setExporting('excel');
    notify('Preparing Excel export…');
    try {
      const url = api.exportGateLogExcel(buildExportParams());
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '', timeout: 120000 });
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 50) throw new Error('Empty file — no records matched your filters');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_GateLog_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify('Excel export downloaded ✓');
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      notify(`Excel export failed: ${msg}`, 'error');
    } finally { setExporting(null); }
  };

  const doExportPdf = async () => {
    setExporting('pdf');
    notify('Preparing PDF export…');
    try {
      const url = api.exportGateLogPdf(buildExportParams());
      const res = await axiosClient.get(url, { responseType: 'blob', baseURL: '', timeout: 120000 });
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('text/plain') || contentType.includes('text/html')) {
        const msg = await res.data.text();
        if (msg.includes('reportlab')) throw new Error('reportlab not installed on server. Run: pip install reportlab');
        throw new Error(msg.slice(0, 200));
      }
      // res.data is already a Blob when responseType:'blob' — do not re-wrap with new Blob([])
      const blob = res.data;
      if (blob.size < 100) throw new Error('Empty file — no records matched your filters');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${getBrandingSlug()}_GateLog_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
      notify('PDF export downloaded ✓');
    } catch(e) {
      const msg = e.name === 'TimeoutError' ? 'Export timed out — try a shorter date range' : await readBlobError(e);
      notify(`PDF export failed: ${msg}`, 'error');
    } finally { setExporting(null); }
  };

  // ── HELPERS ────────────────────────────────────────────────────────────────
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDuration = (v) => {
    if (!v.time_in || !v.time_out) return null;
    const mins = Math.round((new Date(v.time_out) - new Date(v.time_in)) / 60000);
    const h = Math.floor(mins / 60), m = mins % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  const filtered = search.trim()
    ? visitors.filter(v =>
        v.name.toLowerCase().includes(search.toLowerCase()) ||
        v.id_number.toLowerCase().includes(search.toLowerCase()) ||
        (v.car_plate || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.phone || '').includes(search)
      )
    : visitors;

  const todayOut = today.filter(v => v.status === 'out').length;

  const VisitorRow = (v, showExit = true) => [
    <div>
      <div style={{ fontWeight: 700, color: 'var(--textPrimary)', fontSize: 13 }}>{v.name}</div>
      <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>{v.id_number}</div>
    </div>,
    v.car_plate
      ? <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#c45e3e', letterSpacing: 2, background: 'var(--bgInput)', padding: '2px 8px', borderRadius: 3 }}>{v.car_plate}</span>
      : <span style={{ color: 'var(--textFaint)', fontSize: 11 }}>On foot</span>,
    v.phone || <span style={{ color: 'var(--textFaint)' }}>—</span>,
    <span style={{ color: TYPE_COLORS[v.visitor_type] || '#aaa', fontSize: 12 }}>
      {TYPE_ICONS[v.visitor_type]} {TYPE_LABELS[v.visitor_type]}
    </span>,
    v.purpose || <span style={{ color: 'var(--textFaint)' }}>—</span>,
    v.department ? <span style={{ fontSize:11, color:'#9b59b6' }}>{DEPT_LABELS[v.department] || v.department}</span> : <span style={{ color: 'var(--textFaint)' }}>—</span>,
    v.host_name || <span style={{ color: 'var(--textFaint)' }}>—</span>,
    <span style={{ fontSize: 12, color: 'var(--textMuted)' }}>{fmtTime(v.time_in)}</span>,
    v.time_out
      ? <div>
          <div style={{ fontSize: 12, color: 'var(--textMuted)' }}>{fmtTime(v.time_out)}</div>
          {fmtDuration(v) && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>⏱ {fmtDuration(v)}</div>}
        </div>
      : <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 600 }}>● Inside</span>,
    v.logged_by_name || '—',
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {v.car_plate && <button style={{ ...btnSm, background: '#C9A84C', color: '#1B3A2D', borderColor: '#C9A84C', fontSize: 11, fontWeight: 700 }} onClick={() => printGatePass(v)}>🖨 Pass</button>}
      {showExit && v.status === 'in' &&
        <button style={{ ...btnSm, background: '#7c2d12', color: '#fca5a5', fontSize: 11 }} onClick={() => markExit(v.id, v.name)}>Exit →</button>}
    </div>
  ];

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader icon="🔐" title="Gate Control" sub="Visitor registration, QR exit scanning & vehicle log — auto-refreshes every 15s" />

      <StatCards stats={[
        { label:'Currently Inside', value:inside.length,   color:'#22c55e', icon:'🟢', sub:'On premises now' },
        { label:"Today's Entries",  value:today.length,    color:'#3b82f6', icon:'📥', spark:Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-6+i);return visitors.filter(v=>v.time_in?.slice(0,10)===d.toISOString().slice(0,10)).length;}), sub:`${todayOut} exited` },
        { label:'Exited Today',     value:todayOut,        color:'#f59e0b', icon:'📤' },
        { label:'Total in Log',     value:visitors.length, color:'#c45e3e', icon:'📋' },
      ]} />

      <TabBar tabs={[
        { id: 'register', label: 'Register Entry',           icon: '➕' },
        { id: 'scan',     label: `QR Exit Scan`,             icon: '📷' },
        { id: 'inside',   label: `Inside (${inside.length})`, icon: '🟢' },
        { id: 'today',    label: `Today (${today.length})`,   icon: '📅' },
        { id: 'log',      label: 'Full Log',                  icon: '📋' },
        { id: 'export',   label: 'Export',                    icon: '📊' },
        { id: 'analytics', label: 'Visitor Analytics',          icon: '📈' },
      ]} active={tab} onChange={setTab} />

      {/* ══════════════ REGISTER FORM ══════════════ */}
      {tab === 'register' && (
        <div style={{ display: 'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 380px', gap: 20, alignItems: 'start' }}>

          {/* ── LEFT: Form ── */}
          <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {/* Form Header */}
            <div style={{ padding: '18px 24px 16px', borderBottom: '1px solid var(--border)', background: 'linear-gradient(135deg, rgba(196,94,62,0.08) 0%, transparent 60%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(196,94,62,0.15)', border: '1px solid rgba(196,94,62,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔐</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--textPrimary)', letterSpacing: 0.3 }}>New Visitor Entry</div>
                  <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 1 }}>All entries are logged with timestamp</div>
                </div>
              </div>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* Visitor Type Pills */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: 'var(--textMuted)', textTransform: 'uppercase', marginBottom: 10 }}>Visitor Type</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => {
                    const active = form.visitor_type === k;
                    return (
                      <button key={k} onClick={() => setForm({ ...form, visitor_type: k })} style={{
                        padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${active ? TYPE_COLORS[k] : 'var(--border)'}`,
                        background: active ? TYPE_COLORS[k] + '22' : 'transparent',
                        color: active ? TYPE_COLORS[k] : 'var(--textMuted)',
                        fontSize: 12, fontWeight: active ? 700 : 400, cursor: 'pointer',
                        transition: 'all 0.15s', letterSpacing: 0.3,
                      }}>
                        {TYPE_ICONS[k]} {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name + ID row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Full Name *</label>
                  <input ref={nameRef} style={{ ...inputStyle, fontSize: 15, fontWeight: 500 }} value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Enter visitor's full name"
                    onKeyDown={e => e.key === 'Enter' && document.getElementById('id-field')?.focus()} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>ID / Passport *</label>
                  <input id="id-field" style={inputStyle} value={form.id_number}
                    onChange={e => setForm({ ...form, id_number: e.target.value })} placeholder="National ID or Passport"
                    onKeyDown={e => e.key === 'Enter' && document.getElementById('plate-field')?.focus()} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Phone</label>
                  <input style={inputStyle} value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+254 700 000 000" />
                </div>
              </div>

              {/* Vehicle Plate — Hero field */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                  Vehicle Number Plate <span style={{ color: '#c8a96e', fontWeight: 400, fontSize: 9 }}>— Required for visitor pass</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 10, fontWeight: 700, color:'var(--textMuted)', letterSpacing: 0.5
                  }}>🇰🇪</div>
                  <input id="plate-field" style={{
                    ...inputStyle,
                    paddingLeft: 36,
                    textTransform: 'uppercase',
                    fontFamily: '"Courier New", monospace',
                    fontSize: 18, letterSpacing: 5, fontWeight: 900,
                    color: form.car_plate ? '#c8a96e' : 'var(--textMuted)',
                    border: `2px solid ${form.car_plate ? 'rgba(200,169,110,0.5)' : 'var(--border)'}`,
                    borderRadius: 8,
                  }}
                    value={form.car_plate} onChange={e => setPlate(e.target.value)}
                    placeholder="KAA 000A" maxLength={12} />
                  {form.car_plate && (
                    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#c8a96e', fontWeight: 700 }}>✓</div>
                  )}
                </div>
                {!form.car_plate && (
                  <div style={{ fontSize: 10, color: '#c8a96e88', marginTop: 4, fontStyle: 'italic' }}>
                    ⚠ Visitor pass can only be printed with a number plate
                  </div>
                )}
              </div>

              {/* Purpose + Dept + Host */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Purpose of Visit</label>
                  <input style={inputStyle} value={form.purpose}
                    onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Meeting, Delivery…" />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Department</label>
                  <select style={inputStyle} value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                    <option value="">— Select —</option>
                    {Object.entries(DEPT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Host Person</label>
                  <input style={inputStyle} value={form.host_name}
                    onChange={e => setForm({ ...form, host_name: e.target.value })} placeholder="Person being visited" />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--textMuted)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Notes</label>
                  <input style={inputStyle} value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional observations…" />
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button style={{
                  flex: 1, padding: '13px 20px', borderRadius: 8, border: 'none',
                  background: loading ? '#333' : 'linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  letterSpacing: 0.5, boxShadow: loading ? 'none' : '0 4px 14px rgba(196,94,62,0.3)',
                  transition: 'all 0.2s',
                }} onClick={register} disabled={loading}>
                  {loading ? '⏳ Registering…' : '✓  Register & Log Entry'}
                </button>
                <button style={{
                  padding: '13px 18px', borderRadius: 8,
                  background: 'var(--bgInput)', border: '1px solid var(--border)',
                  color: 'var(--textMuted)', fontSize: 13, cursor: 'pointer',
                }} onClick={() => setForm(blankForm)}>Clear</button>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Pass Preview + Today Stats ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {lastEntry ? (
              <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                {/* Pass header */}
                <div style={{
                  padding: '16px 20px', textAlign: 'center',
                  background: `linear-gradient(135deg, ${TYPE_COLORS[lastEntry.visitor_type]}18 0%, transparent 60%)`,
                  borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 32, marginBottom: 6 }}>{TYPE_ICONS[lastEntry.visitor_type]}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--textPrimary)', letterSpacing: 0.3 }}>{lastEntry.name}</div>
                  <div style={{ display: 'inline-block', marginTop: 6, padding: '3px 12px', borderRadius: 12, background: TYPE_COLORS[lastEntry.visitor_type] + '22', border: `1px solid ${TYPE_COLORS[lastEntry.visitor_type]}44`, fontSize: 10, fontWeight: 700, color: TYPE_COLORS[lastEntry.visitor_type], textTransform: 'uppercase', letterSpacing: 2 }}>
                    {TYPE_LABELS[lastEntry.visitor_type]}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: '#22c55e', fontWeight: 600 }}>● Logged {fmtTime(lastEntry.time_in)}</div>
                </div>

                {/* Pass body */}
                <div style={{ padding: '14px 20px' }}>
                  {[
                    ['ID', lastEntry.id_number],
                    lastEntry.phone && ['Phone', lastEntry.phone],
                    lastEntry.purpose && ['Purpose', lastEntry.purpose],
                    lastEntry.host_name && ['Host', lastEntry.host_name],
                  ].filter(Boolean).map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                      <span style={{ color: 'var(--textMuted)', fontWeight: 500 }}>{l}</span>
                      <span style={{ color: 'var(--textPrimary)' }}>{v}</span>
                    </div>
                  ))}

                  {/* Plate display */}
                  {lastEntry.car_plate && (
                    <div style={{ margin: '12px 0', padding: '10px 14px', background: 'rgba(200,169,110,0.07)', border: '1px solid rgba(200,169,110,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 10, color:'var(--textMuted)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Vehicle</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 900, color: '#c8a96e', letterSpacing: 4 }}>{lastEntry.car_plate}</span>
                    </div>
                  )}

                  {/* QR Code */}
                  {lastEntry.qr_token && (
                    <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 4 }}>
                      <div style={{ fontSize: 9, color: 'var(--textMuted)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>QR Exit Code</div>
                      <div style={{ background: '#fff', borderRadius: 10, padding: 10, display: 'inline-block', border: '2px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                        <QRCodeDisplay token={lastEntry.qr_token} size={140} />
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--textFaint)', marginTop: 6, fontFamily: 'monospace' }}>
                        Pass #{lastEntry.id} · {lastEntry.qr_token.slice(0, 8)}…
                      </div>
                    </div>
                  )}

                  {/* Print button — only if has plate */}
                  <div style={{ marginTop: 14 }}>
                    {lastEntry.car_plate ? (
                      <button style={{
                        width: '100%', padding: '11px 16px', borderRadius: 8, border: 'none',
                        background: '#C9A84C', color: '#1B3A2D',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(201,168,76,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        fontFamily: 'inherit',
                      }} onClick={() => printGatePass(lastEntry)}>
                        🖨 Print Visitor Pass
                      </button>
                    ) : (
                      <div style={{ padding: '10px 14px', background: 'rgba(201,168,76,0.06)', border: '1.5px dashed rgba(201,168,76,0.25)', borderRadius: 8, fontSize: 12, color: 'var(--textMuted)', textAlign: 'center', lineHeight: 1.6 }}>
                        <span style={{fontSize:14}}>🚗</span><br/>
                        A vehicle number plate is required<br/>to generate a visitor pass
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🪪</div>
                <div style={{ color: 'var(--textMuted)', fontSize: 13, fontWeight: 500 }}>Visitor pass will appear here</div>
                <div style={{ color: 'var(--textFaint)', fontSize: 11, marginTop: 4 }}>after first registration</div>
              </div>
            )}

            {/* Today by Type */}
            <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bgHeader)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Today by Type</div>
              </div>
              <div style={{ padding: '8px 18px 12px' }}>
                {Object.entries(TYPE_LABELS).map(([key, label]) => {
                  const count = today.filter(v => v.visitor_type === key).length;
                  if (!count) return null;
                  const pct = today.length > 0 ? Math.round(count / today.length * 100) : 0;
                  return (
                    <div key={key} style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: TYPE_COLORS[key], fontWeight: 600 }}>{TYPE_ICONS[key]} {label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--textPrimary)' }}>{count}</span>
                      </div>
                      <div style={{ height: 3, background: 'var(--bgInput)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: TYPE_COLORS[key], borderRadius: 2, transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  );
                })}
                {today.length === 0 && (
                  <div style={{ color: 'var(--textFaint)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No entries today yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ══════════════ QR EXIT SCAN ══════════════ */}
      {tab === 'scan' && (
        <div style={{ display: 'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 1fr', gap: 16 }} className="layout-sidebar">
          <Card title="📷 QR Exit Scanner">

            {/* ── Camera viewfinder ── */}
            {cameraOpen && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ position: 'relative', background: '#000', borderRadius: 10, overflow: 'hidden', aspectRatio: '16/9' }}>
                  <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} playsInline muted />
                  {/* Corner scan-frame overlay */}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ width: 180, height: 180, position: 'relative' }}>
                      {/* top-left */}
                      <div style={{ position:'absolute', top:0, left:0, width:32, height:32, borderTop:'3px solid #22c55e', borderLeft:'3px solid #22c55e' }} />
                      {/* top-right */}
                      <div style={{ position:'absolute', top:0, right:0, width:32, height:32, borderTop:'3px solid #22c55e', borderRight:'3px solid #22c55e' }} />
                      {/* bottom-left */}
                      <div style={{ position:'absolute', bottom:0, left:0, width:32, height:32, borderBottom:'3px solid #22c55e', borderLeft:'3px solid #22c55e' }} />
                      {/* bottom-right */}
                      <div style={{ position:'absolute', bottom:0, right:0, width:32, height:32, borderBottom:'3px solid #22c55e', borderRight:'3px solid #22c55e' }} />
                    </div>
                  </div>
                  {camScanning && (
                    <div style={{ position:'absolute', bottom:12, left:0, right:0, textAlign:'center', color:'#22c55e', fontSize:12, fontWeight:700, letterSpacing:1 }}>
                      ● SCANNING…
                    </div>
                  )}
                </div>
                <canvas ref={canvasRef2} style={{ display: 'none' }} />
                <button onClick={stopCamera} style={{ ...btnSm, width:'100%', marginTop:8, background:'#7c2d12', color:'#fca5a5' }}>
                  ✕ Close Camera
                </button>
              </div>
            )}

            {cameraError && (
              <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#ef4444' }}>
                📵 {cameraError}
              </div>
            )}

            {/* Camera launch button */}
            {!cameraOpen && (
              <button onClick={startCamera} style={{ ...btnPrimary, width:'100%', marginBottom:14, fontSize:14, padding:'13px', background:'#166534', letterSpacing:0.5 }}>
                📱 Scan with Phone / Webcam Camera
              </button>
            )}

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, color:'var(--textFaint)', fontSize:12 }}>
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
              <span>or enter manually</span>
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
            </div>

            <Field label="Paste / type QR token or URL">
              <input
                ref={qrInputRef}
                style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 14, letterSpacing: 1 }}
                value={qrInput}
                onChange={e => setQrInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && qrInput.trim()) handleQrScan(qrInput); }}
                placeholder="USB scanner or paste token here, then press Enter…"
                autoComplete="off"
              />
            </Field>

            <button
              style={{ ...btnPrimary, width: '100%', marginTop: 10, fontSize: 14,
                background: qrInput.trim() ? 'var(--primary)' : 'var(--bgInput)',
                color: qrInput.trim() ? '#fff' : 'var(--textFaint)',
                cursor: qrInput.trim() ? 'pointer' : 'default' }}
              onClick={() => qrInput.trim() && handleQrScan(qrInput)}
              disabled={scanLoading || !qrInput.trim()}>
              {scanLoading ? '🔍 Looking up…' : '🔍 Look Up Pass'}
            </button>

            {/* Scan tips */}
            <div style={{ marginTop: 20, fontSize: 12, color: 'var(--textMuted)', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--textSecond)' }}>💡 Tips</div>
              <div>• 📱 Camera scan works on any phone or laptop with a webcam</div>
              <div>• 🔌 USB handheld scanners auto-submit on scan</div>
              <div>• ✏️ Token is the 32-char code on the visitor's pass</div>
            </div>
          </Card>

          {/* Scan result / action panel */}
          <div>
            {scanError && !scanResult && (
              <Card title="Scan Result">
                <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>❌</div>
                  <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 15 }}>{scanError}</div>
                  <button style={{ ...btnSm, marginTop: 16 }} onClick={() => { setScanError(''); setScanResult(null); qrInputRef.current?.focus(); }}>
                    ← Try Again
                  </button>
                </div>
              </Card>
            )}

            {scanResult && (
              <Card title={scanResult.status === 'out' ? '✓ Already Exited' : '👤 Visitor Found'}>
                {/* Visitor info */}
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 4 }}>{TYPE_ICONS[scanResult.visitor_type]}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--textPrimary)', marginBottom: 2 }}>{scanResult.name}</div>
                  <div style={{ fontSize: 12, color: TYPE_COLORS[scanResult.visitor_type], textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
                    {TYPE_LABELS[scanResult.visitor_type]}
                  </div>
                </div>

                <div style={{ background: 'var(--bgInput)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  {[
                    ['Pass #',      `#${scanResult.id}`],
                    ['ID / Pass',   scanResult.id_number],
                    scanResult.car_plate && ['Vehicle', scanResult.car_plate],
                    ['Time In',     fmtTime(scanResult.time_in)],
                    scanResult.time_out && ['Time Out', fmtTime(scanResult.time_out)],
                    scanResult.time_out && ['Duration', fmtDuration(scanResult)],
                    scanResult.department && ['Department', DEPT_LABELS[scanResult.department] || scanResult.department],
                    ['Status',      scanResult.status === 'in' ? '🟢 Inside' : '⬛ Exited'],
                    ['Logged By',   scanResult.logged_by_name],
                  ].filter(Boolean).map(([l, v]) => v && (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span style={{ color: 'var(--textMuted)', fontWeight: 600 }}>{l}</span>
                      <span style={{ color: 'var(--textPrimary)', fontFamily: l === 'Vehicle' ? 'monospace' : 'inherit', fontWeight: l === 'Pass #' ? 700 : 400 }}>{v}</span>
                    </div>
                  ))}
                </div>

                {scanError && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#f59e0b' }}>
                    ⚠️ {scanError}
                  </div>
                )}

                {/* Action buttons */}
                {scanResult.status === 'in' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button
                      onClick={confirmExitScan}
                      disabled={scanLoading}
                      style={{ padding: '14px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 15, letterSpacing: 0.5 }}>
                      {scanLoading ? 'Recording…' : '✓ CONFIRM EXIT'}
                    </button>
                    <button
                      onClick={() => { setScanResult(null); setScanError(''); qrInputRef.current?.focus(); }}
                      style={{ padding: '14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bgInput)', color: 'var(--textMuted)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                      ← Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ textAlign: 'center', padding: '10px', background: 'rgba(100,116,139,0.1)', borderRadius: 6, fontSize: 13, color: 'var(--textMuted)' }}>
                      Exit already recorded at {fmtTime(scanResult.time_out)}
                    </div>
                    <button
                      onClick={() => { setScanResult(null); setScanError(''); qrInputRef.current?.focus(); }}
                      style={{ ...btnPrimary, width: '100%' }}>
                      ← Scan Another
                    </button>
                  </div>
                )}
              </Card>
            )}

            {!scanResult && !scanError && (
              <Card title="Awaiting Scan">
                <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--textFaint)' }}>
                  <div style={{ fontSize: 64, marginBottom: 12, opacity: 0.5 }}>📷</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--textMuted)' }}>Ready to scan</div>
                  <div style={{ fontSize: 13 }}>Scan the QR code on the visitor's pass<br />or type the token and press Enter</div>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ INSIDE NOW ══════════════ */}
      {tab === 'inside' && (
        <div>
          {/* Live status banner */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 10, marginBottom: 16 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.25)', animation: 'pulse 2s infinite' }} />
            <div style={{ fontSize: 13, color: '#22c55e', fontWeight: 600 }}>{inside.length} visitor{inside.length !== 1 ? 's' : ''} currently on premises</div>
            <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--textFaint)' }}>Auto-refreshes every 15s</div>
          </div>
          {inside.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12 }}>
              <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🟢</div>
              <div style={{ color: 'var(--textMuted)', fontSize: 14 }}>No visitors currently inside</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
              {inside.map(v => {
                const typeColor = TYPE_COLORS[v.visitor_type] || '#888';
                const elapsed = v.time_in ? Math.round((Date.now() - new Date(v.time_in).getTime()) / 60000) : 0;
                const elHours = Math.floor(elapsed / 60);
                const elMins = elapsed % 60;
                const elStr = elHours > 0 ? `${elHours}h ${elMins}m` : `${elMins}m`;
                return (
                  <div key={v.id} style={{
                    background: 'var(--bgCard)', border: `1px solid ${typeColor}33`,
                    borderTop: `3px solid ${typeColor}`,
                    borderRadius: 10, padding: '14px 16px',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{ position: 'absolute', top: 10, right: 12, width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 2px rgba(34,197,94,0.25)' }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                      <div style={{ fontSize: 22, lineHeight: 1 }}>{TYPE_ICONS[v.visitor_type]}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--textPrimary)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</div>
                        <div style={{ fontSize: 10, color: typeColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>{TYPE_LABELS[v.visitor_type]}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--textMuted)', marginBottom: 8 }}>
                      {v.id_number && <div>ID: {v.id_number}</div>}
                      {v.purpose && <div style={{ marginTop: 2 }}>📌 {v.purpose}</div>}
                      {v.host_name && <div style={{ marginTop: 2 }}>👤 {v.host_name}</div>}
                    </div>
                    {v.car_plate && (
                      <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 900, color: '#c8a96e', letterSpacing: 3, padding: '4px 8px', background: 'rgba(200,169,110,0.08)', border: '1px solid rgba(200,169,110,0.2)', borderRadius: 5, display: 'inline-block', marginBottom: 8 }}>
                        {v.car_plate}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>Time In</div>
                        <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 700 }}>{fmtTime(v.time_in)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>On premises</div>
                        <div style={{ fontSize: 12, color: 'var(--textPrimary)', fontWeight: 700 }}>{elStr}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                      {v.car_plate && <button style={{ ...btnSm, fontSize: 10, flex: 1 }} onClick={() => printGatePass(v)}>🖨 Pass</button>}
                      <button style={{ ...btnSm, background: '#7c2d12', color: '#fca5a5', fontSize: 10, flex: 1 }} onClick={() => markExit(v.id, v.name)}>Exit →</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ TODAY'S LOG ══════════════ */}
      {tab === 'today' && (
        <Card title={`Today's Log — ${new Date().toLocaleDateString('en-KE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}>
          {today.length === 0 ? <Empty text="No entries today" /> :
            <Table
              headers={['Name / ID', 'Plate', 'Phone', 'Type', 'Purpose', 'Dept', 'Host', 'Time In', 'Time Out', 'Officer', 'Actions']}
              rows={today.map(v => VisitorRow(v, true))}
            />
          }
        </Card>
      )}

      {/* ══════════════ FULL LOG ══════════════ */}
      {tab === 'log' && (
        <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: 'var(--bgHeader)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)' }}>Full Visitor Log</div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--textMuted)' }}>🔍</span>
                <input style={{ ...inputStyle, paddingLeft: 32, width: '100%' }} placeholder="Search by name, ID, plate or phone…"
                  value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            {/* Date filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 11, color: 'var(--textMuted)', whiteSpace: 'nowrap' }}>From</label>
              <input type="date" style={{ ...inputStyle, width: 140 }} value={logDateFrom}
                onChange={e => setLogDateFrom(e.target.value)} />
              <label style={{ fontSize: 11, color: 'var(--textMuted)', whiteSpace: 'nowrap' }}>To</label>
              <input type="date" style={{ ...inputStyle, width: 140 }} value={logDateTo}
                onChange={e => setLogDateTo(e.target.value)} />
              {(logDateFrom || logDateTo) && (
                <button style={{ fontSize: 11, padding: '4px 10px', background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--textMuted)', cursor: 'pointer' }}
                  onClick={() => { setLogDateFrom(''); setLogDateTo(''); }}>✕ Clear</button>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)', fontWeight: 600, background: 'var(--bgInput)', padding: '4px 10px', borderRadius: 20 }}>
              {filtered.length} records
            </div>
          </div>
          <div style={{ padding: '0 4px' }}>
            {filtered.length === 0 ? <Empty text="No records found" /> :
              <Table
                headers={['Name / ID', 'Plate', 'Phone', 'Type', 'Purpose', 'Dept', 'Host', 'Time In', 'Time Out', 'Officer', 'Actions']}
                rows={filtered.map(v => VisitorRow(v, false))}
              />
            }
          </div>
        </div>
      )}

      {/* ══════════════ EXPORT ══════════════ */}
      {tab === 'export' && (
        <div style={{ display: 'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '1fr 1fr', gap: 16 }} className="layout-sidebar">
          <Card title="📊 Export Gate Log">

            <div style={{ fontSize: 12, color: 'var(--textMuted)', marginBottom: 16, lineHeight: 1.7 }}>
              Exports include: name, ID, phone, plate, type, department, purpose, host, time in/out, duration, status, and officer.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginBottom: 20 }}>
              <Field label="Date From">
                <input type="date" style={inputStyle} value={exportDateFrom} onChange={e => setExportDateFrom(e.target.value)} />
              </Field>
              <Field label="Date To">
                <input type="date" style={inputStyle} value={exportDateTo} onChange={e => setExportDateTo(e.target.value)} />
              </Field>
              <Field label="Visitor Type">
                <select style={inputStyle} value={exportType} onChange={e => setExportType(e.target.value)}>
                  <option value="">All types</option>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select style={inputStyle} value={exportStatus} onChange={e => setExportStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  <option value="in">Currently Inside</option>
                  <option value="out">Exited</option>
                </select>
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                style={{ ...btnPrimary, fontSize: 14, padding: '13px 10px', background: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
                onClick={doExportExcel}
                disabled={!!exporting}>
                {exporting === 'excel'
                  ? <><span style={{ fontSize: 16 }}>⏳</span> Exporting…</>
                  : <><span style={{ fontSize: 16 }}>📗</span> Download Excel</>}
              </button>
              <button
                style={{ ...btnPrimary, fontSize: 14, padding: '13px 10px', background: '#7c2d12', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
                onClick={doExportPdf}
                disabled={!!exporting}>
                {exporting === 'pdf'
                  ? <><span style={{ fontSize: 16 }}>⏳</span> Exporting…</>
                  : <><span style={{ fontSize: 16 }}>📕</span> Download PDF</>}
              </button>
            </div>

            <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 6, fontSize: 11, color: 'var(--textMuted)', lineHeight: 1.7 }}>
              📗 <strong>Excel</strong> — full data table with summary sheet, sortable and filterable<br/>
              📕 <strong>PDF</strong> — formatted report with summary stats, ready to print or share
            </div>
          </Card>

          {/* Live stats */}
          <Card title="Gate Log Summary">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Total Logged', value: visitors.length, color: '#3b82f6' },
                { label: 'Inside Now',   value: inside.length,   color: '#22c55e' },
                { label: 'Exited Today', value: todayOut,        color: '#f59e0b' },
                { label: "Today's Total",value: today.length,    color: '#c45e3e' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bgInput)', border: `1px solid ${s.color}33`, borderTop: `3px solid ${s.color}`, borderRadius: 8, padding: '14px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, color: 'var(--textMuted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Today by visitor type
            </div>
            {Object.entries(TYPE_LABELS).map(([key, label]) => {
              const count = today.filter(v => v.visitor_type === key).length;
              if (!count) return null;
              const pct = today.length ? Math.round(count / today.length * 100) : 0;
              return (
                <div key={key} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                    <span style={{ color: TYPE_COLORS[key] }}>{TYPE_ICONS[key]} {label}</span>
                    <span style={{ color: 'var(--textPrimary)', fontWeight: 700 }}>{count}</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--bgHover)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: TYPE_COLORS[key], borderRadius: 4, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
            {today.length === 0 && <div style={{ color: 'var(--textFaint)', textAlign: 'center', padding: '20px 0', fontSize: 13 }}>No entries today</div>}
          </Card>
        </div>
      )}

      {/* ── ANALYTICS TAB ── */}
      {tab === 'analytics' && (
        <div style={{ display:'grid', gap:16 }}>
          {/* Controls */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <select style={{ ...inputStyle, width:'auto', padding:'7px 12px', fontSize:12 }}
              value={analyticsDays} onChange={e => setAnalyticsDays(Number(e.target.value))}>
              <option value={0}>All Time</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <select style={{ ...inputStyle, width:'auto', padding:'7px 12px', fontSize:12 }}
              value={analyticsLimit} onChange={e => setAnalyticsLimit(Number(e.target.value))}>
              <option value={10}>Top 10</option>
              <option value={20}>Top 20</option>
              <option value={50}>Top 50</option>
            </select>
            <button style={{ ...btnPrimary, padding:'7px 18px', fontSize:12 }} onClick={loadAnalytics}>
              {analyticsLoading ? '⏳ Loading…' : '↻ Refresh'}
            </button>
            {analytics && (
              <span style={{ fontSize:11, color:'var(--textMuted)', marginLeft:'auto' }}>
                {analytics.total_unique} unique visitors · {analytics.total_visits} total visits
                {analyticsDays > 0 ? ` in last ${analyticsDays} days` : ' (all time)'}
              </span>
            )}
          </div>

          {/* Summary cards */}
          {analytics && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12 }}>
              {[
                { label:'Unique Visitors', value: analytics.total_unique, icon:'👤', color:'#3498db' },
                { label:'Total Visits', value: analytics.total_visits, icon:'🔁', color:'#c8a96e' },
                { label:'Repeat Visitors', value: analytics.results?.length || 0, icon:'⭐', color:'#2ecc71' },
                { label:'Avg Visits (repeat)', value: analytics.results?.length
                    ? (analytics.results.reduce((s,v)=>s+v.visit_count,0)/analytics.results.length).toFixed(1)
                    : '—', icon:'📊', color:'#9b59b6' },
              ].map(s => (
                <div key={s.label} style={{ background:'var(--bgCard)', border:`1px solid ${s.color}33`, borderTop:`3px solid ${s.color}`, borderRadius:8, padding:'14px 12px', textAlign:'center' }}>
                  <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
                  <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Frequent visitors table */}
          <Card title={`⭐ Frequent Visitors — Top ${analyticsLimit}`}>
            {analyticsLoading ? (
              <div style={{ textAlign:'center', padding:'30px 0', color:'var(--textMuted)', fontSize:13 }}>Loading…</div>
            ) : !analytics?.results?.length ? (
              <Empty text="No repeat visitors found for this period" />
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:'2px solid var(--border)' }}>
                      {['#','Name','ID / Passport','Phone','Type','Visits','Avg Stay','Total Time','Departments Visited','Last Seen','First Visit'].map(h => (
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, color:'var(--textMuted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.results.map((v, i) => {
                      const fmtMins = (m) => m == null ? '—' : m >= 60 ? `${Math.floor(m/60)}h ${m%60}m` : `${m}m`;
                      const typeColor = TYPE_COLORS[v.visitor_type] || '#888';
                      return (
                        <tr key={v.id_number} style={{ borderBottom:'1px solid var(--border)', background: i%2===0?'transparent':'rgba(0,0,0,0.02)' }}>
                          <td style={{ padding:'9px 10px', color:'var(--textFaint)', fontSize:11, fontWeight:700 }}>{i+1}</td>
                          <td style={{ padding:'9px 10px', fontWeight:700, color:'var(--textPrimary)', whiteSpace:'nowrap' }}>
                            {v.name}
                            {v.visit_count >= 10 && <span style={{ marginLeft:5, fontSize:9, background:'#c8a96e22', color:'#c8a96e', padding:'1px 5px', borderRadius:3, fontWeight:700 }}>VIP</span>}
                          </td>
                          <td style={{ padding:'9px 10px', fontFamily:'monospace', fontSize:11, color:'var(--textSecond)' }}>{v.id_number}</td>
                          <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontSize:11 }}>{v.phone || '—'}</td>
                          <td style={{ padding:'9px 10px' }}>
                            <span style={{ fontSize:10, fontWeight:700, background:typeColor+'22', color:typeColor, padding:'2px 7px', borderRadius:3 }}>
                              {v.type_label}
                            </span>
                          </td>
                          <td style={{ padding:'9px 10px', textAlign:'center' }}>
                            <span style={{ fontSize:15, fontWeight:900, color: v.visit_count>=10?'#c8a96e':v.visit_count>=5?'#2ecc71':'var(--textPrimary)' }}>
                              {v.visit_count}
                            </span>
                          </td>
                          <td style={{ padding:'9px 10px', color:'var(--textSecond)', whiteSpace:'nowrap' }}>{fmtMins(v.avg_duration_mins)}</td>
                          <td style={{ padding:'9px 10px', color:'var(--textMuted)', whiteSpace:'nowrap' }}>{fmtMins(v.total_time_mins)}</td>
                          <td style={{ padding:'9px 10px', maxWidth:160 }}>
                            {v.departments.length > 0
                              ? v.departments.map(d => (
                                  <span key={d} style={{ display:'inline-block', fontSize:9, background:'var(--bgInput)', color:'var(--textMuted)', padding:'1px 5px', borderRadius:3, marginRight:3, marginBottom:2, textTransform:'capitalize' }}>
                                    {d.replace('_',' ')}
                                  </span>
                                ))
                              : <span style={{ color:'var(--textFaint)' }}>—</span>}
                          </td>
                          <td style={{ padding:'9px 10px', color:'var(--textMuted)', fontSize:11, whiteSpace:'nowrap' }}>{v.last_seen || '—'}</td>
                          <td style={{ padding:'9px 10px', color:'var(--textFaint)', fontSize:11, whiteSpace:'nowrap' }}>{v.first_seen || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── QR Code Display Component ─────────────────────────────────────────────────
function QRCodeDisplay({ token, size = 160 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !token) return;
    // Use real QR library from utils/qrcode.js (#10/#67)
    try {
      qrToCanvas(null, canvasRef.current, { text: token, size });
    } catch (e) {
      // fallback: draw an error indicator
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#f00';
      ctx.font = '12px sans-serif';
      ctx.fillText('QR error', 8, size / 2);
    }
  }, [token, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block', imageRendering: 'pixelated' }}
      title={`QR token: ${token}`}
    />
  );
}